import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { api } from "@/lib/api";
import { Sparkles, Send, X, MessageSquarePlus, Trash2, Bot, User as UserIcon, Loader2 } from "lucide-react";

const MODELS = [
  { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 (Anthropic)" },
  { value: "gpt-5-2", label: "GPT-5.2 (OpenAI)" },
];

/**
 * Floating assistant widget available on every authenticated page.
 * - Bottom-right button opens a docked chat panel.
 * - Sessions persisted in Mongo (per-user).
 * - Optional RAG context: set window.__assistantContext = { dossier_id, prospect_id, label } to seed context.
 */
const AssistantWidget = forwardRef(function AssistantWidget(props, ref) {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [ragContext, setRagContext] = useState(null); // { dossier_id?, prospect_id?, label }
  const [quota, setQuota] = useState(null); // { used, limit, remaining, pending }
  const [limitReached, setLimitReached] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const scrollRef = useRef(null);

  const loadQuota = async () => {
    try {
      const { data } = await api.get("/assistant/quota");
      setQuota(data);
      setLimitReached(data.remaining <= 0);
    } catch (_e) { /* ignore */ }
  };

  const requestMore = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/assistant/request-more");
      loadQuota();
      setMessages((m) => [...m, { role: "assistant",
        content: data.already_pending
          ? "⏳ Une demande est déjà en attente de validation par l'administration."
          : "✅ Votre demande de déblocage a été envoyée à l'administration. Vous pourrez continuer une fois validée.",
        created_at: new Date().toISOString() }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `Erreur : ${err.response?.data?.detail || "demande impossible"}`, created_at: new Date().toISOString() }]);
    } finally {
      setRequesting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openWithContext: (ctx) => { setRagContext(ctx); setOpen(true); newConversation(); },
  }));

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/assistant/sessions");
      setSessions(data);
    } catch (_e) {
      // ignore
    }
  };

  useEffect(() => {
    if (open) { loadSessions(); loadQuota(); }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openSession = async (session_id) => {
    setActiveSession(session_id);
    if (!session_id) {
      setMessages([]);
      return;
    }
    const { data } = await api.get(`/assistant/sessions/${session_id}/messages`);
    setMessages(data);
  };

  const newConversation = () => {
    setActiveSession(null);
    setMessages([]);
    setInput("");
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", content: input, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setSending(true);
    const text = input;
    setInput("");
    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: activeSession, message: text, model,
        dossier_id: ragContext?.dossier_id, prospect_id: ragContext?.prospect_id,
      });
      setActiveSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, created_at: new Date().toISOString(), model: data.model }]);
      loadSessions();
      loadQuota();
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitReached(true);
        loadQuota();
      }
      setMessages((m) => [...m, { role: "assistant", content: `${err.response?.data?.detail || "assistant indisponible"}`, created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (session_id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/assistant/sessions/${session_id}`);
    if (activeSession === session_id) newConversation();
    loadSessions();
  };

  const SUGGESTIONS = [
    "Comment relancer efficacement un prospect chaud qui ne répond plus ?",
    "Rédige un argumentaire pour un lot en VEFA à un investisseur",
    "Quelles questions poser lors d'un premier rendez-vous closer ?",
    "Comment gérer un dossier financement refusé par la banque ?",
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open-button"
          className="fixed bottom-6 right-40 z-40 bg-[#111111] text-white px-4 py-3 rounded-sm shadow-2xl hover:bg-slate-800 transition-all flex items-center gap-2 border border-[#B8873A]/40"
        >
          <Sparkles className="w-4 h-4 text-[#B8873A]" />
          <span className="text-sm font-semibold tracking-wide">Assistant IA</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-0 right-0 md:bottom-20 md:right-6 z-40 w-full md:w-[560px] h-[86vh] md:h-[640px] bg-white rounded-sm shadow-2xl border border-slate-200 flex flex-col" data-testid="assistant-panel">
          {/* Header */}
          <div className="bg-[#111111] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#B8873A]" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Le Kiosque · IA</div>
                <div className="text-sm font-bold tracking-tight" style={{fontFamily:"Cabinet Grotesk"}}>Assistant conversationnel</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {quota && (
                <span className="text-[10px] bg-slate-800 border border-slate-700 text-slate-300 rounded-sm px-2 py-1" data-testid="assistant-quota-badge" title="Messages IA restants aujourd'hui">
                  {quota.remaining}/{quota.limit} aujourd'hui
                </span>
              )}
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                data-testid="assistant-model-select"
                className="bg-slate-800 text-white text-xs border border-slate-700 rounded-sm px-2 py-1"
              >
                {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <button onClick={() => setOpen(false)} data-testid="assistant-close-button" className="text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Context banner */}
          {ragContext?.label && (
            <div className="bg-[#B8873A]/10 border-b border-[#B8873A]/30 px-4 py-2 flex items-center justify-between gap-2" data-testid="assistant-context-banner">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-3 h-3 text-[#B8873A] shrink-0" />
                <div className="text-xs text-[#111111] truncate">
                  <span className="font-semibold">Contexte actif :</span> {ragContext.label}
                </div>
              </div>
              <button onClick={() => setRagContext(null)} className="text-xs text-slate-500 hover:text-slate-800 shrink-0">✕</button>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 flex min-h-0">
            {/* Sessions sidebar */}
            <div className="w-40 border-r border-slate-200 flex flex-col bg-slate-50">
              <button
                onClick={newConversation}
                data-testid="assistant-new-session-button"
                className="m-2 flex items-center gap-1 px-2 py-2 bg-[#111111] text-white rounded-sm text-xs font-semibold hover:bg-slate-800"
              >
                <MessageSquarePlus className="w-3 h-3" /> Nouvelle
              </button>
              <div className="overflow-y-auto flex-1">
                {sessions.length === 0 ? (
                  <div className="text-xs text-slate-400 text-center px-2 py-4">Aucune conversation</div>
                ) : sessions.map((s) => (
                  <div
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    className={`px-2 py-2 text-xs cursor-pointer border-b border-slate-100 group flex items-start gap-1 ${activeSession === s.session_id ? "bg-white border-l-2 border-l-[#B8873A]" : "hover:bg-white"}`}
                    data-testid={`assistant-session-${s.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-700 line-clamp-2">{s.last_message}</div>
                      <div className="text-[9px] text-slate-400 mt-1">{new Date(s.updated_at).toLocaleDateString("fr-FR")} · {s.count} msgs</div>
                    </div>
                    <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 flex flex-col min-w-0">
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && !sending && (
                  <div className="text-center py-4">
                    <Bot className="w-10 h-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
                    <div className="text-sm text-slate-700 font-semibold mb-1">Bonjour, comment puis-je vous aider ?</div>
                    <div className="text-xs text-slate-500 mb-4">Posez une question métier, sur l&apos;application, ou sur un dossier.</div>
                    <div className="space-y-1.5">
                      {SUGGESTIONS.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(s)}
                          className="block w-full text-left text-xs bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-100 text-slate-700"
                          data-testid={`assistant-suggestion-${i}`}
                        >{s}</button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`assistant-message-${i}`}>
                    <div className={`shrink-0 w-7 h-7 rounded-sm flex items-center justify-center ${m.role === "user" ? "bg-[#B8873A] text-white" : "bg-[#111111] text-[#B8873A]"}`}>
                      {m.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                    </div>
                    <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#111111] text-white" : "bg-slate-100 text-slate-800 border border-slate-200"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}

                {sending && (
                  <div className="flex gap-2">
                    <div className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center bg-[#111111] text-[#B8873A]">
                      <Sparkles className="w-3.5 h-3.5" />
                    </div>
                    <div className="bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-600 flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Rédaction…
                    </div>
                  </div>
                )}
              </div>

              {/* Input / quota */}
              {limitReached ? (
                <div className="border-t border-slate-200 p-3 bg-amber-50" data-testid="assistant-limit-banner">
                  <div className="text-xs text-amber-900 mb-2">
                    Vous avez atteint votre limite de <strong>{quota?.limit ?? 10} messages IA</strong> aujourd'hui.
                    {quota?.pending ? " Demande en attente de validation." : " Demandez un déblocage à l'administration pour continuer."}
                  </div>
                  <button
                    onClick={requestMore}
                    disabled={requesting || quota?.pending}
                    data-testid="assistant-request-more-button"
                    className="w-full bg-[#B8873A] text-white px-3 py-2 rounded-sm text-sm font-semibold hover:bg-[#a0752f] disabled:opacity-50"
                  >
                    {quota?.pending ? "Demande envoyée ⏳" : requesting ? "Envoi…" : "Demander un déblocage à l'administration"}
                  </button>
                </div>
              ) : (
              <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Votre question…"
                  data-testid="assistant-input"
                  disabled={sending}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  data-testid="assistant-send-button"
                  className="bg-[#111111] text-white px-3 py-2 rounded-sm hover:bg-slate-800 disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default AssistantWidget;
